import SectionDivider from 'components/shared/SectionDivider';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import { ReactNode, useState } from 'react';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import ProResponse from 'types/ProResponse';
import { validatePaymentForm } from 'utils/PaymentFormHelper';
import CreditCardForm from 'components/forms/CreditCardForm';
import CountryCode from 'types/CountryCode';
import useUser from 'hooks/useUser';
import getFormattedCvvCode from './utils/getFormattedCvvCode';
import ApplePayButton from './ApplePayButton';
import ErrorText from './ErrorText';
import * as styles from './PaymentForm.module.scss';
import PayPalButton from './PayPalButton';
import GooglePayButton from './GooglePayButton';
import LoadingOverlay from './LoadingOverlay';

type Props = {
  billingCountry: CountryCode;
  coupon: Coupon;
  currencyCode: CurrencyCode;
  cvv: string;
  cvvError: ReactNode;
  errors: ProResponse['errors'];
  expiration: string;
  expirationError: ReactNode;
  name: string;
  nameError: ReactNode;
  number: string;
  numberError: ReactNode;
  plan: Plan;
  postalCode: string;
  setName: (name: string) => void;
  setNumber: (number: string) => void;
  setPostalCode: (postalCode: string) => void;
  userId: number;
  setCvv: (cvv: string) => void;
  setNumberError: (error: ReactNode) => void;
  setExpiration: (expiration: string) => void;
  setNameError: (error: ReactNode) => void;
  postalCodeError: ReactNode;
  setPostalCodeError: (error: ReactNode) => void;
  setErrors: (errors: ProResponse['errors']) => void;
  setCvvError: (error: ReactNode) => void;
  setBillingCountry: (billingCountry: CountryCode) => void;
  setExpirationError: (error: ReactNode) => void;
  setCurrencyCode: (currencyCode: CurrencyCode) => void;
  setNonTrialPlan: (plan: Plan) => void;
  setPlan: (plan: Plan) => void;
  multiyearConversion?: boolean;
};

export default function PaymentForm({
  billingCountry,
  coupon,
  currencyCode,
  plan,
  userId,
  errors,
  setErrors,
  expiration,
  number,
  cvv,
  postalCode,
  name,
  setExpiration,
  setNumber,
  setCvv,
  setPostalCode,
  setName,
  setNumberError,
  numberError,
  nameError,
  setNameError,
  setCvvError,
  cvvError,
  postalCodeError,
  setPostalCodeError,
  expirationError,
  setBillingCountry,
  setExpirationError,
  setCurrencyCode,
  setNonTrialPlan,
  setPlan,
  multiyearConversion
}: Props) {
  const user = useUser();
  // this submitting state is used for non-creditcard services
  const [submitting, setSubmitting] = useState<boolean>(false);

  function handleCreditCardFormChange(event: any) {
    const { name, value } = event.target;
    const payload = validatePaymentForm(name, value, {});

    switch (name) {
      case 'number': {
        /* 
          Handles the case where the user switches from an amex card number to any other card type, 
          which requires a different cvv character length.
        */
        const formattedCvv = getFormattedCvvCode(cvv, value);
        if (cvv !== formattedCvv) {
          setCvv(formattedCvv);
        }
        setNumber(value);
        break;
      }
      case 'expiration':
        setExpiration(value);
        break;
      case 'cvv': {
        const formattedCvv = getFormattedCvvCode(value, number);
        setCvv(formattedCvv);
        setCvvError(payload.errors.cvv);
        break;
      }
      case 'postalCode':
        setPostalCode(value);
        setPostalCodeError(payload.errors.postalCode);
        break;
      case 'name':
        setName(value);
        setNameError(payload.errors.name);
        break;
      default:
        throw new Error(`Missing credit card form name ${name}`);
    }
  }

  return (
    <>
      <div className={styles.grid}>
        <ApplePayButton
          billingCountry={billingCountry}
          coupon={coupon}
          currencyCode={currencyCode}
          multiyearConversion={multiyearConversion}
          plan={plan}
          setErrors={setErrors}
          userId={userId}
          setSubmitting={setSubmitting}
        />
        <GooglePayButton
          billingCountry={billingCountry}
          coupon={coupon}
          currencyCode={currencyCode}
          multiyearConversion={multiyearConversion}
          plan={plan}
          setErrors={setErrors}
          userId={userId}
          setSubmitting={setSubmitting}
        />
        <PayPalButton
          billingCountry={billingCountry}
          coupon={coupon}
          currencyCode={currencyCode}
          multiyearConversion={multiyearConversion}
          plan={plan}
          setErrors={setErrors}
          userId={userId}
          setSubmitting={setSubmitting}
          submitting={submitting}
        />
      </div>
      <SectionDivider classes={styles.divider}>
        <FormattedMessage defaultMessage="Or enter payment details" />
      </SectionDivider>
      <CreditCardForm
        billingCountry={{
          onChange: (value, response) => {
            setBillingCountry(value);
            setCurrencyCode(response.currencyCode);
            setNonTrialPlan(response.nonTrial);

            // similar logic in pro_controllers_helper.rb for initial load wonder if we can consolidate in one spot?
            if (!coupon && (!user || user.isPromoEligible)) {
              setPlan(response.trial);
            } else {
              setPlan(response.nonTrial);
            }
          },
          value: billingCountry
        }}
        changeHandler={handleCreditCardFormChange}
        cvv={cvv}
        errors={{ expiration: expirationError, name: nameError, number: numberError, cvv: cvvError, postalCode: postalCodeError }}
        expiration={expiration}
        isCouponInputExpanded={false}
        isCouponInputShown={false}
        name={name}
        number={number}
        postalCode={postalCode}
        setError={(name, error) => {
          // these are then only two errors set on the credit card form
          switch (name) {
            case 'expiration':
              setExpirationError(error);
              break;
            case 'number':
              setNumberError(error);
              break;
            default:
              throw new Error(`missing credit card form error handling for name '${name}'`);
          }
        }}
      />
      {errors && <ErrorText errors={errors} />}
      {submitting && <LoadingOverlay />}
    </>
  );
}
